/**
 * EmptyState, friendly empty-state pattern (UX-DESIGN.md § Empty states).
 *
 *   Lightweight icon + 1 sentence + optional CTA.
 *   Never used to display "0" or "," as primary content; only when the
 *   underlying collection IS empty.
 *
 * Push #14: Prefer the `kind` prop over the raw `icon` prop. When `kind`
 * is provided, the icon renders as a soft-variant ObjectIcon (lg / 56px,
 * 10% tinted background + colored stroke) — same color vocabulary as the
 * page-header ObjectIcon for visual rhyme. Falls back to the legacy
 * `icon` prop for surfaces that haven't migrated yet (no breaking change).
 *
 * Push 4-1ai (2026-07-10): added `primaryAction` + `secondaryAction`
 * structured props so callsites don't have to hand-roll a ButtonCluster
 * or Link wrapper per empty state. Also added `variant='inline'` for
 * placements inside existing cards (drops the dashed-border card chrome).
 * Legacy `cta` prop preserved for surfaces that pre-date this extension.
 */

import type { ReactNode } from 'react';

import { ObjectIcon, type ObjectKind } from './ObjectIcon.tsx';
import { type UiLinkComponent, useUiLink } from './UiLink.tsx';

/**
 * Push 4-1ai (2026-07-10): structured action shape for empty-state CTAs.
 * `to` renders the app's injected link component (see UiLink.tsx) — client-side
 * nav when the app provides a router link, a plain `<a href>` otherwise.
 * `onClick` renders a `<button>` — use when the action opens a modal or
 * fires a callback (e.g. a create-modal opener). At least one of `to`
 * or `onClick` must be provided; if both are set, `to` wins.
 */
export interface EmptyStateAction {
  label: string;
  to?: string;
  onClick?: () => void;
}

interface EmptyStateProps {
  /** Preferred: ObjectIcon kind. Renders soft-variant 56px tile. */
  kind?: ObjectKind;
  /** Legacy: raw icon node. Only used when `kind` is not provided. */
  icon?: ReactNode;
  title: string;
  body?: string;
  /**
   * Structured primary action (Push 4-1ai). Rendered as `btn btn-primary`.
   * Preferred over `cta` for typical "browse the market" / "invite your
   * team" affordances so the button styling stays consistent.
   */
  primaryAction?: EmptyStateAction;
  /**
   * Structured secondary action (Push 4-1ai). Rendered as `btn btn-secondary`
   * to the right of `primaryAction`.
   */
  secondaryAction?: EmptyStateAction;
  /**
   * Legacy escape hatch for arbitrary CTA markup (multiple buttons, custom
   * badges, etc.). Rendered below the title + body. Prefer `primaryAction`
   * + `secondaryAction` for standard shapes.
   */
  cta?: ReactNode;
  /**
   * Push 4-1ai (2026-07-10): `'card'` (default) wraps in the standard
   * `.empty-state` dashed-border hero. `'inline'` drops the wrapper for
   * placement inside an existing `.card` (e.g. a widget in a wider grid)
   * — avoids the double-nested-card look.
   */
  variant?: 'card' | 'inline';
}

function renderAction(
  action: EmptyStateAction,
  klass: string,
  LinkComponent: UiLinkComponent,
): ReactNode {
  if (action.to !== undefined) {
    return (
      <LinkComponent to={action.to} className={klass}>
        {action.label}
      </LinkComponent>
    );
  }
  return (
    <button type="button" className={klass} onClick={action.onClick}>
      {action.label}
    </button>
  );
}

export function EmptyState({
  kind,
  icon,
  title,
  body,
  primaryAction,
  secondaryAction,
  cta,
  variant = 'card',
}: EmptyStateProps) {
  const LinkComponent = useUiLink();
  const inner = (
    <>
      {kind !== undefined ? (
        <div className="empty-state-icon" style={{ display: 'flex', justifyContent: 'center' }}>
          <ObjectIcon kind={kind} size="lg" variant="soft" />
        </div>
      ) : (
        icon !== undefined && <div className="empty-state-icon">{icon}</div>
      )}
      <h3 className="empty-state-title">{title}</h3>
      {body !== undefined && <p className="empty-state-body">{body}</p>}
      {(primaryAction !== undefined || secondaryAction !== undefined) && (
        <div
          className="empty-state-actions"
          style={{ display: 'flex', gap: 8, justifyContent: 'center', flexWrap: 'wrap' }}
        >
          {primaryAction !== undefined &&
            renderAction(primaryAction, 'btn btn-primary btn-sm', LinkComponent)}
          {secondaryAction !== undefined &&
            renderAction(secondaryAction, 'btn btn-secondary btn-sm', LinkComponent)}
        </div>
      )}
      {cta}
    </>
  );

  if (variant === 'inline') {
    // No dashed-border card chrome — sits inside an existing card so we
    // don't double-nest borders.
    return (
      <div
        className="empty-state empty-state-inline"
        style={{ background: 'transparent', border: 'none', padding: '24px 12px' }}
      >
        {inner}
      </div>
    );
  }

  return <div className="empty-state">{inner}</div>;
}

// Push 5-post-smoke-25a (2026-07-14) — legacy local Icon* exports removed.
// The `kind` prop (ObjectIcon soft-variant, 56px tile) is the canonical
// path for empty-state visuals. The old locally-scoped 24-grid Lucide
// SVGs (IconInbox / IconPlug / IconUsers / IconActivity / IconSparkle /
// IconBook) had zero remaining callsites and duplicated names already
// live in Icon.tsx — keeping them here would drift the canonical
// vocabulary. Removed to eliminate the parallel icon set.
